import React, { memo, useRef } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

const stripQuery = (path = "") => path.split("?")[0].toLowerCase();

// /dashboard hosts every Agents sub-tab (?d=<alias>), each with its own chat
// and insights, so those need one cached instance per sub-tab.
const getCacheKey = (location) => {
  const pathname = location.pathname.toLowerCase();
  if (pathname !== "/dashboard") return pathname;
  const params = new URLSearchParams(location.search);
  const dashboard = params.get("d") || params.get("dashboard_name");
  return dashboard ? `${pathname}?${dashboard}` : pathname;
};

// <Routes location> hands its subtree a brand-new LocationContext value on
// every render, so an unmemoized pane would re-render every hidden tab (and
// re-fire their location-dependent hooks) on each navigation. Memoizing on the
// pinned location means a pane only renders when its own location changes.
const TabPane = memo(({ element, location }) => (
  <Routes location={location}>
    <Route path="*" element={element} />
  </Routes>
));

/**
 * Keeps every visited tab mounted (hidden with display:none) so switching
 * tabs does not discard chat history, loaded insights, or in-flight
 * generations. Hidden tabs are pinned to the location they were last active
 * at so they do not react to navigation that belongs to another tab.
 */
const KeepAliveTabs = ({ tabs }) => {
  const location = useLocation();
  const cacheRef = useRef(new Map());
  const cache = cacheRef.current;

  const activeKey = getCacheKey(location);
  const activeTab = tabs.find(
    (tab) => stripQuery(tab.path) === location.pathname.toLowerCase(),
  );

  if (activeTab?.component) {
    cache.set(activeKey, { path: activeTab.path, location });
  }
  // The bare /dashboard entry only redirects to the first sub-tab.
  if (activeKey !== "/dashboard") cache.delete("/dashboard");

  return Array.from(cache.entries()).map(([key, entry]) => {
    const tab = tabs.find((t) => t.path === entry.path);
    if (!tab?.component) return null;
    const isActive = key === activeKey;
    return (
      <div
        key={key}
        style={{ display: isActive ? "block" : "none", height: "100%" }}
      >
        <TabPane element={tab.component} location={entry.location} />
      </div>
    );
  });
};

export default KeepAliveTabs;
